import * as angular from 'angular';
import * as momentTz from 'moment-timezone';
import { Component, Input, Output, Inject, EventEmitter, OnInit, OnChanges } from 'ng-metadata/core';
import { DateRangeService } from '../date-range.service'
import './date-range-selector.component.scss';

/**
 * @ngdoc directive
 * @name mwDateRangeSelector
 * @module mw.components.dateRange
 *
 * @param {Object} [date-range] - A selected date range option.  If provided, this will show a checkmark next to the appropriate date range option in the menu.  The date range option object should have the following structure, {name: string, type: string, startDate: Date, endDate: Date}.
 * @param {string|Object[]} [date-range-types] - Array of Date Range type options to display in the menu.  Supported values include a set of predefined types or custom Date Range option object.  Predefined types include 'today', 'thisWeek', 'last7Days', 'last14Days', 'thisMonth', 'last30Days', 'last90Days', 'thisQuarter', 'lastQuarter', 'thisYear', 'lastYear' and 'custom'.  (Default: =['today','thisWeek','last7Days', 'last14Days','thisMonth','last30Days', 'divider', 'custom']).  The custom date range option object should have the following structure, {name: string, type: string, startDate?: Date, endDate?: Date}.
 * @param {Date} [relative-date=new Date()] - The date that all relative date range type options will be relative to.
 * @param {string} [hover-date-format] - The format of label on hover.
 * @param {boolean} [no-padding=false] - Indicates if the modal should not have any padding.
 * @param {string|number} [width] - determines the width of the date-range-selector dropdown when input is selected
 * @param {string} [locale] - 'The language that the date picker will be shown in'
 * @param {string} [timezone] - Timezone to be used by date range selector. Valid momentjs
 *    timezones (see https://momentjs.com/timezone/docs/#/using-timezones/parsing-in-zone/) are supported.  If not specified, the timezone of the browser will be used.
 * @param {Object} [custom-range-picker-options] - custom options related to the date-range picker, includes locale, start-date, end-date, min-view, max-view, min-date, max-date, max-days
 * @param {Object} [custom-range-dialog-options] - custom options related to the dialog of the date range picker
 * @param {boolean} [limit-end-to-now=false] - If true, future date/time is not allowed and the end date time will automtically be set to 'now' (the moment the user selects the date range)
 * @param {expression} [on-select] - Angular expression to evaluate on select of a date range menu item. $event is the DateRangeOption object representing the selected date range option.
 *
 * @description
 * `<mw-date-range-selector>` provides a selectable list of common date ranges.  It can be used in various containers such as menus, accordions or content panels.
 *
 * @usage
 * <hljs hljs-language="html">
 *   <mw-date-range-selector date-range="$ctrl.selectedDateRange" on-select="$ctrl.selectedDateRange = $event"></mw-date-range-menu>
 * </hljs>
 *
 */
@Component({
    selector: 'mw-date-range-selector',
    template: `
        <div class="mw-date-range-selector mw-menu-dropdown" ng-class="{'mw-date-range-selector--no-padding': $ctrl.noPadding}" ng-style="{'width': $ctrl.width + 'px'}">
            <mw-menu-item type="focusdummy" md-autofocus></mw-menu-item>
            <mw-menu-item
                ng-repeat="dateRangeOption in $ctrl.dateRangeOptions"
                type="{{dateRangeOption.type === 'divider' ? 'divider' : 'singleselect'}}"
                hover-label="{{($ctrl.relativeDate && $ctrl.locale && $ctrl.timezone) ? $ctrl.getHoverLabel(dateRangeOption) : ''}}"
                is-selected="$ctrl.dateRange.name ? ($ctrl.dateRange.name === dateRangeOption.name): ($ctrl.dateRange.type === dateRangeOption.type)"
                on-click="$ctrl.selectDateRangeOption(dateRangeOption)"
                aria-label="{{dateRangeOption.name | translate}}">
                <span translate="{{dateRangeOption.name}}"></span><span ng-if="dateRangeOption.name === 'Custom'">...</span>
            </mw-menu-item>
        </div>
    `
})
export class DateRangeSelectorComponent implements OnInit {
    @Input('<') customRangeDialogOptions = {};
    @Input('<') customRangePickerOptions = {};
    @Input('<') dateRange: DateRangeSelectorOption;
    @Input('<') dateRangeTypes: (DateRangeSelectorOption|DateRangeType|'divider')[];
    @Input('<') hoverDateFormat: String = 'MMM D';
    @Input('<') noPadding: boolean;
    @Input('<') width: (number|string);
    @Input('<') relativeDate: Date;
    @Input('<') locale: string;
    @Input('<') timezone: string;
    @Input('<') limitEndToNow: boolean;
    @Output() onSelect: EventEmitter<DateRange> = new EventEmitter<DateRange>();

    private dateRangeOptions: DateRangeSelectorOption[] = [];
    private dateRangeOptionPresets: {[key: string]: DateRangeSelectorOption} = {
        today: {
            name: 'Today',
            type: 'today'
        },
        yesterday: {
            name: 'Yesterday',
            type: 'yesterday'
        },
        thisWeek: {
            name: 'This week',
            type: 'thisWeek'
        },
        last7Days: {
            name: 'Last 7 days',
            type: 'last7Days'
        },
        last14Days: {
            name: 'Last 14 days',
            type: 'last14Days'
        },
        thisMonth: {
            name: 'This month',
            type: 'thisMonth'
        },
        last30Days: {
            name: 'Last 30 days',
            type: 'last30Days'
        },
        lastMonth: {
            name: 'Last month',
            type: 'lastMonth'
        },
        thisQuarter: {
            name: 'This quarter',
            type: 'thisQuarter'
        },
        last90Days: {
            name: 'Last 90 days',
            type: 'last90Days'
        },
        lastQuarter: {
            name: 'Last quarter',
            type: 'lastQuarter'
        },
        thisYear: {
            name: 'This year',
            type: 'thisYear'
        },
        lastYear: {
            name: 'Last year',
            type: 'lastYear'
        },
        divider: {
            name: '',
            type: 'divider'
        },
        custom: {
            name: 'Custom',
            type: 'custom'
        }
    };

    constructor(
        @Inject('mwDateRangeService') private dateRangeService: DateRangeService,
        @Inject('mwDateRangePickerDialogService') private mwDateRangePickerDialogService,
        @Inject('$filter') private $filter: ng.IFilterService,
        @Inject('$translate') private $translate,
        @Inject('$element') private $element
    ) {
        'ngInject';
    }

    public ngOnInit() {
        this.locale = this.locale ? this.locale : this.$translate.use() || this.$translate.proposedLanguage() || 'en';
        this.timezone = this.timezone ? this.timezone : momentTz.tz.guess(); // If no timezone is passed in, let moment guess the timezone
        this.relativeDate = momentTz(this.relativeDate).tz(this.timezone).toDate();
        this.noPadding = this.noPadding === true;
        this.limitEndToNow = this.limitEndToNow === true;
        this.dateRangeTypes = this.dateRangeTypes && this.dateRangeTypes.length ? this.dateRangeTypes : ['today', 'thisWeek', 'last7Days', 'thisMonth', 'last30Days', 'divider', 'custom'];
        this.dateRangeOptions = this.dateRangeTypes.map((dateRangeType) => {
            let dateRange;

            if (typeof dateRangeType === 'string') {
                dateRange = this.dateRangeOptionPresets[dateRangeType];
            } else {
                dateRange = dateRangeType;
            }

            return {
                name: dateRange.name,
                type: dateRange.type,
                separator: dateRange.separator,
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                useCustomPicker: dateRange.useCustomPicker
            };
        });
    }

    private getHoverLabel(dateRangeSelectorOption: DateRangeSelectorOption) {
        let dateRange;

        // Don't love this conditional logic, at some point should make this more clear/concise if possible
        if (dateRangeSelectorOption.type === 'custom') {
            if (dateRangeSelectorOption.startDate && dateRangeSelectorOption.endDate) {
                dateRange = dateRangeSelectorOption;
            } else if (this.dateRange && this.dateRange.name === dateRangeSelectorOption.name) {
                dateRange = this.dateRange;
            }
        } else if (dateRangeSelectorOption.type !== 'divider') {
            let hoverDate = this.relativeDate ? this.relativeDate : new Date();
            dateRange = this.dateRangeService[dateRangeSelectorOption.type](hoverDate, this.timezone, this.locale);
        }

        return dateRange ? this.$filter('mwDateRange')(dateRange, this.hoverDateFormat, this.timezone, this.locale) : '';
    }

    private selectDateRangeOption(dateRangeSelectorOption: DateRangeSelectorOption): void {
        let selectedDateRange;

        if (dateRangeSelectorOption.useCustomPicker) {
            selectedDateRange = {
                type: dateRangeSelectorOption.type,
                name: dateRangeSelectorOption.name,
                useCustomPicker: dateRangeSelectorOption.useCustomPicker
            };
            this.onSelect.emit(selectedDateRange);
        } else if (dateRangeSelectorOption.startDate && dateRangeSelectorOption.endDate) {
            selectedDateRange = {
                startDate: dateRangeSelectorOption.startDate,
                endDate: dateRangeSelectorOption.endDate,
                type: dateRangeSelectorOption.type,
                name: dateRangeSelectorOption.name
            };

            this.onSelect.emit(selectedDateRange);
        } else if (dateRangeSelectorOption.name === 'Custom') {
            if (this.dateRange && this.dateRange.name === 'Custom') {
                this.selectCustomDateRange(dateRangeSelectorOption, this.dateRange);
            } else {
                this.selectCustomDateRange(dateRangeSelectorOption);
            }
        } else if (this.dateRangeService[dateRangeSelectorOption.type]) {
            selectedDateRange = this.dateRangeService[dateRangeSelectorOption.type](this.relativeDate, this.timezone, this.locale);
            selectedDateRange.name = dateRangeSelectorOption.name;

            if (this.limitEndToNow) {
                selectedDateRange = this.dateRangeService.limitEndToNow(selectedDateRange);
            }

            this.onSelect.emit(selectedDateRange);
        }
    }

    private selectCustomDateRange(dateRangeSelectorOption: DateRangeSelectorOption, currentDateRange?: DateRangeSelectorOption, event?: ng.IAngularEvent): void {
        let dialogOptions = {
            targetEvent: event
        };
        dialogOptions = angular.extend(dialogOptions, this.customRangeDialogOptions);

        let dateRangePickerOptions = {
            locale: this.locale,
            startView: 'month',
            minView: 'year',
            maxView: 'month',
            startDate: currentDateRange && currentDateRange.name === 'Custom' ? currentDateRange.startDate : undefined,
            endDate: currentDateRange  && currentDateRange.name === 'Custom' ? currentDateRange.endDate : undefined,
            maxDate: this.limitEndToNow ? momentTz().tz(this.timezone) : null,
            timezone: this.timezone
        };
        dateRangePickerOptions = angular.extend(dateRangePickerOptions, this.customRangePickerOptions);

        this.mwDateRangePickerDialogService.show(dialogOptions, dateRangePickerOptions)
            .then((dateRange) => {
                dateRange.name = dateRangeSelectorOption.name;

                if (this.limitEndToNow) {
                    dateRange = this.dateRangeService.limitEndToNow(dateRange);
                }

                this.onSelect.emit(dateRange);
            })
            .catch(() => {
                // In the event the user cancels date range selection, nothing to do
            });
    }
}

interface DateRangeSelectorOption {
    name?: string;
    type: DateRangeType | 'divider';
    startDate?: Date;
    endDate?: Date;
    relativeRangeFilter?: string;
    useCustomPicker?: boolean
}



// WEBPACK FOOTER //
// ./src/lib/components/date-range/date-range-selector/date-range-selector.component.ts